/* ============================================================
   TEACHU.AI — THE GILDED THREAD
   Scroll-scrubbed WebGL journey: a practice, upgraded.
   Stages: 01 scattered → 02 threaded → 03 upgraded → 04 operating
   Three.js r128 (UMD) + GSAP ScrollTrigger. No modules, no build.
   ============================================================ */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var stageEl = document.querySelector('[data-tx-experience]');

    /* ---------- Header state (runs on every page) ---------- */
    var header = document.querySelector('.tx-header');
    if (header) {
      var onScrollHeader = function () {
        header.classList.toggle('is-scrolled', window.scrollY > 24);
      };
      window.addEventListener('scroll', onScrollHeader, { passive: true });
      onScrollHeader();
    }

    if (!stageEl) return; // not on the homepage

    var canvas = document.getElementById('teachu-canvas');
    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function webglOK() {
      try {
        var c = document.createElement('canvas');
        return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
      } catch (e) { return false; }
    }

    var hasDeps = typeof window.THREE !== 'undefined' && typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';

    if (prefersReduced || !webglOK() || !hasDeps || !canvas) {
      document.body.classList.add('no-webgl');
      window.__tx = { ready: true, mode: 'fallback' };
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    window.__tx = { ready: true, mode: 'webgl' };

    /* ============================================================
       SCENE
       ============================================================ */
    var ABYSS = 0x0a1420, NAVY = 0x16324f, GOLD = 0xb48a3c, GOLD_L = 0xd3b26a, IVORY = 0xf5f1e8;

    var renderer = new THREE.WebGLRenderer({
      canvas: canvas, antialias: true, alpha: false, powerPreference: 'high-performance'
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(ABYSS, 1);

    var scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(ABYSS, 0.055);

    var camera = new THREE.PerspectiveCamera(42, window.innerWidth / window.innerHeight, 0.1, 200);

    /* ---------- The thread: one continuous gilded path ---------- */
    var pathPoints = [
      new THREE.Vector3(-14, -2.2, 6),
      new THREE.Vector3(-8, 1.4, 1.5),
      new THREE.Vector3(-2.5, -1.2, -3.5),
      new THREE.Vector3(3.5, 1.8, -8),
      new THREE.Vector3(9, -0.6, -13.5),
      new THREE.Vector3(13.5, 1.2, -19),
      new THREE.Vector3(16, 0.2, -26),
      new THREE.Vector3(16.5, 0.6, -33)
    ];
    var curve = new THREE.CatmullRomCurve3(pathPoints, false, 'catmullrom', 0.6);

    var TUBE_SEGS = 640;
    var coreGeo = new THREE.TubeGeometry(curve, TUBE_SEGS, 0.035, 8, false);
    var coreMat = new THREE.MeshBasicMaterial({
      color: GOLD_L, transparent: true, opacity: 0.95,
      blending: THREE.AdditiveBlending, depthWrite: false
    });
    var threadCore = new THREE.Mesh(coreGeo, coreMat);
    scene.add(threadCore);

    var haloGeo = new THREE.TubeGeometry(curve, TUBE_SEGS, 0.16, 8, false);
    var haloMat = new THREE.MeshBasicMaterial({
      color: GOLD, transparent: true, opacity: 0.10,
      blending: THREE.AdditiveBlending, depthWrite: false
    });
    var threadHalo = new THREE.Mesh(haloGeo, haloMat);
    scene.add(threadHalo);

    var coreIndexCount = coreGeo.index.count;
    var haloIndexCount = haloGeo.index.count;
    coreGeo.setDrawRange(0, 0);
    haloGeo.setDrawRange(0, 0);

    /* ---------- Glow sprite texture (shared) ---------- */
    function makeGlowTexture(inner, outer) {
      var c = document.createElement('canvas');
      c.width = c.height = 128;
      var g = c.getContext('2d');
      var grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
      grad.addColorStop(0, inner);
      grad.addColorStop(0.35, outer);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, 128, 128);
      var tex = new THREE.CanvasTexture(c);
      return tex;
    }
    var glowTex = makeGlowTexture('rgba(255,244,214,1)', 'rgba(211,178,106,0.55)');

    function makeSprite(scale, opacity) {
      var mat = new THREE.SpriteMaterial({
        map: glowTex, color: 0xffffff, transparent: true, opacity: opacity,
        blending: THREE.AdditiveBlending, depthWrite: false
      });
      var s = new THREE.Sprite(mat);
      s.scale.set(scale, scale, 1);
      return s;
    }

    var headPulse = makeSprite(1.6, 0.95);   // rides the tip of the drawing thread
    scene.add(headPulse);
    var verifyPulse = makeSprite(0.9, 0.0);  // verification pass, loops the drawn thread in stage 3+
    scene.add(verifyPulse);

    /* ---------- Document texture (ivory card, gold rules) ---------- */
    function makeDocTexture() {
      var c = document.createElement('canvas');
      c.width = 128; c.height = 168;
      var g = c.getContext('2d');
      g.fillStyle = '#f5f1e8';
      g.fillRect(0, 0, 128, 168);
      g.strokeStyle = 'rgba(180,138,60,0.85)';
      g.lineWidth = 2;
      g.strokeRect(3, 3, 122, 162);
      g.fillStyle = 'rgba(22,50,79,0.55)';
      for (var i = 0; i < 7; i++) {
        var w = 88 - (i % 3) * 18;
        g.fillRect(18, 34 + i * 16, w, 3);
      }
      g.fillStyle = 'rgba(180,138,60,0.9)';
      g.fillRect(18, 14, 34, 6);
      return new THREE.CanvasTexture(c);
    }
    var docTex = makeDocTexture();

    /* ---------- The document field: scatter → align → lattice ---------- */
    var DOCS = 84;
    var docGroup = new THREE.Group();
    scene.add(docGroup);

    var docGeo = new THREE.PlaneGeometry(0.62, 0.82);
    var rng = mulberry32(20260703);
    function mulberry32(a) {
      return function () {
        a |= 0; a = (a + 0x6D2B79F5) | 0;
        var t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    }
    function r(min, max) { return min + rng() * (max - min); }

    // Lattice destination: ordered grid near the late path (the "upgraded system")
    var latticeCenter = curve.getPointAt(0.78).clone().add(new THREE.Vector3(0, 0.2, 0));
    var COLS = 7, ROWS = 4, LAYERS = 3, GAP = 1.05;

    var docs = [];
    for (var i = 0; i < DOCS; i++) {
      var m = new THREE.Mesh(docGeo, new THREE.MeshBasicMaterial({
        map: docTex, side: THREE.DoubleSide, transparent: true,
        opacity: 0.85, depthWrite: false
      }));

      // Stage A: scattered chaos around the early path
      var a = curve.getPointAt(r(0.02, 0.3)).clone()
        .add(new THREE.Vector3(r(-6.5, 6.5), r(-4, 4), r(-6, 6)));

      // Stage B: loosely threaded — flanking the mid path
      var tMid = r(0.32, 0.62);
      var side = (i % 2 === 0 ? 1 : -1);
      var b = curve.getPointAt(tMid).clone()
        .add(new THREE.Vector3(side * r(1.2, 2.6), r(-1.4, 1.4), side * r(-0.6, 0.6)));

      // Stage C: the lattice
      var ci = i % COLS, riw = Math.floor(i / COLS) % ROWS, li = Math.floor(i / (COLS * ROWS)) % LAYERS;
      var cPos = new THREE.Vector3(
        latticeCenter.x + (ci - (COLS - 1) / 2) * GAP,
        latticeCenter.y + (riw - (ROWS - 1) / 2) * GAP,
        latticeCenter.z + (li - (LAYERS - 1) / 2) * GAP * 1.15
      );

      m.position.copy(a);
      var rotA = new THREE.Euler(r(-1.4, 1.4), r(-1.4, 1.4), r(-0.9, 0.9));
      var rotB = new THREE.Euler(r(-0.25, 0.25), r(-0.4, 0.4) + (side > 0 ? -0.5 : 0.5), 0);
      var rotC = new THREE.Euler(0, 0, 0);
      m.rotation.copy(rotA);

      docs.push({
        mesh: m, a: a, b: b, c: cPos, rotA: rotA, rotB: rotB, rotC: rotC,
        drift: r(0.4, 1.2), phase: r(0, Math.PI * 2)
      });
      docGroup.add(m);
    }

    /* ---------- Lattice frame: the vault takes shape in stage 3 ---------- */
    var frameSize = new THREE.Vector3(COLS * GAP + 1.2, ROWS * GAP + 1.2, LAYERS * GAP * 1.15 + 1.2);
    var frameGeo = new THREE.BoxGeometry(frameSize.x, frameSize.y, frameSize.z);
    var frameEdges = new THREE.EdgesGeometry(frameGeo);
    var frame = new THREE.LineSegments(frameEdges, new THREE.LineBasicMaterial({
      color: GOLD, transparent: true, opacity: 0.0
    }));
    frame.position.copy(latticeCenter);
    frame.scale.setScalar(0.001);
    scene.add(frame);

    /* ---------- The operating core: end-state monolith ---------- */
    var coreEnd = curve.getPointAt(1.0).clone();
    var coreMesh = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.15, 1),
      new THREE.MeshBasicMaterial({ color: GOLD_L, wireframe: true, transparent: true, opacity: 0.0 })
    );
    coreMesh.position.copy(coreEnd);
    scene.add(coreMesh);

    var coreHalo = makeSprite(6.5, 0.0);
    coreHalo.position.copy(coreEnd);
    scene.add(coreHalo);

    var ringA = new THREE.Mesh(
      new THREE.TorusGeometry(2.0, 0.012, 8, 120),
      new THREE.MeshBasicMaterial({ color: GOLD, transparent: true, opacity: 0.0, blending: THREE.AdditiveBlending, depthWrite: false })
    );
    ringA.position.copy(coreEnd);
    ringA.rotation.x = Math.PI / 2.4;
    scene.add(ringA);

    var ringB = ringA.clone();
    ringB.material = ringA.material.clone();
    ringB.scale.setScalar(1.35);
    ringB.rotation.x = -Math.PI / 3;
    scene.add(ringB);

    /* ---------- Ambient particles ---------- */
    var P = 900;
    var pGeo = new THREE.BufferGeometry();
    var pPos = new Float32Array(P * 3);
    for (var p = 0; p < P; p++) {
      var pt = curve.getPointAt(rng());
      pPos[p * 3] = pt.x + r(-9, 9);
      pPos[p * 3 + 1] = pt.y + r(-6, 6);
      pPos[p * 3 + 2] = pt.z + r(-9, 9);
    }
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    var pMat = new THREE.PointsMaterial({
      color: GOLD_L, size: 0.035, transparent: true, opacity: 0.4,
      blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true
    });
    var particles = new THREE.Points(pGeo, pMat);
    scene.add(particles);

    /* ---------- Camera rail: offset curve above/behind the thread ---------- */
    var camPoints = pathPoints.map(function (pnt, idx) {
      var off = idx < 3 ? new THREE.Vector3(-1.5, 1.6, 7.5) : (idx < 6 ? new THREE.Vector3(-3.2, 1.9, 6.5) : new THREE.Vector3(-4.5, 1.4, 7.5));
      return pnt.clone().add(off);
    });
    var camCurve = new THREE.CatmullRomCurve3(camPoints, false, 'catmullrom', 0.6);

    /* ============================================================
       CHOREOGRAPHY — scroll drives everything
       ============================================================ */
    var progress = { t: 0 };
    var mouse = { x: 0, y: 0, tx: 0, ty: 0 };

    window.addEventListener('pointermove', function (e) {
      mouse.tx = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.ty = (e.clientY / window.innerHeight - 0.5) * 2;
    }, { passive: true });

    function smoothstep(a, b, x) {
      var t = Math.min(1, Math.max(0, (x - a) / (b - a)));
      return t * t * (3 - 2 * t);
    }

    var fogAbyss = new THREE.Color(ABYSS);
    var fogWarm = new THREE.Color(0x14202e);
    var tmpV = new THREE.Vector3();
    var lookV = new THREE.Vector3();

    var railFill = document.querySelector('.tx-rail__fill');

    function applyProgress(t) {
      /* thread draw */
      var draw = smoothstep(0.02, 0.96, t);
      coreGeo.setDrawRange(0, Math.floor(coreIndexCount * draw));
      haloGeo.setDrawRange(0, Math.floor(haloIndexCount * draw));

      /* head pulse rides the tip */
      var tipT = Math.max(0.001, Math.min(0.999, draw));
      curve.getPointAt(tipT, tmpV);
      headPulse.position.copy(tmpV);
      headPulse.material.opacity = 0.55 + 0.4 * Math.sin(perf.now * 0.006);

      /* stage weights */
      var w2 = smoothstep(0.18, 0.42, t);   // scatter -> threaded
      var w3 = smoothstep(0.46, 0.70, t);   // threaded -> lattice
      var w4 = smoothstep(0.78, 0.96, t);   // lattice recedes, core rises

      /* documents morph */
      for (var i = 0; i < docs.length; i++) {
        var d = docs[i];
        var m = d.mesh;

        m.position.lerpVectors(d.a, d.b, w2);
        if (w3 > 0) m.position.lerp(d.c, w3);

        /* idle drift, damped as order takes hold */
        var idle = (1 - w3) * 0.9 + 0.05;
        m.position.y += Math.sin(perf.now * 0.0006 * d.drift + d.phase) * 0.08 * idle;

        m.rotation.x = lerpAngle(lerpAngle(d.rotA.x, d.rotB.x, w2), d.rotC.x, w3);
        m.rotation.y = lerpAngle(lerpAngle(d.rotA.y, d.rotB.y, w2), d.rotC.y, w3);
        m.rotation.z = lerpAngle(lerpAngle(d.rotA.z, d.rotB.z, w2), d.rotC.z, w3);

        m.material.opacity = 0.85 - w4 * 0.55;
      }

      /* frame assembles */
      var fs = 0.001 + w3 * (1 - 0.15 * w4);
      frame.scale.setScalar(fs);
      frame.material.opacity = w3 * 0.55 * (1 - w4 * 0.6);
      frame.rotation.y = w3 * 0.12 + perf.now * 0.00004;

      /* verification pulse: loops the drawn thread once order exists */
      var vOn = smoothstep(0.55, 0.62, t) * (1 - smoothstep(0.94, 0.99, t));
      verifyPulse.material.opacity = vOn * 0.9;
      if (vOn > 0.001) {
        var loopT = (perf.now * 0.00012) % 1;
        curve.getPointAt(Math.min(loopT, tipT), tmpV);
        verifyPulse.position.copy(tmpV);
      }

      /* the operating core */
      coreMesh.material.opacity = w4 * 0.9;
      coreMesh.scale.setScalar(0.4 + w4 * 0.8);
      coreMesh.rotation.y = perf.now * 0.00025;
      coreMesh.rotation.x = perf.now * 0.00013;
      coreHalo.material.opacity = w4 * 0.5;
      ringA.material.opacity = w4 * 0.5;
      ringB.material.opacity = w4 * 0.32;
      ringA.rotation.z = perf.now * 0.00035;
      ringB.rotation.z = -perf.now * 0.00028;

      /* atmosphere */
      scene.fog.color.copy(fogAbyss).lerp(fogWarm, w3 * 0.7);
      scene.fog.density = 0.055 - 0.018 * w4;
      pMat.opacity = 0.4 - 0.22 * w3 + 0.1 * w4;

      /* camera on rails + parallax */
      camCurve.getPointAt(t, tmpV);
      mouse.x += (mouse.tx - mouse.x) * 0.05;
      mouse.y += (mouse.ty - mouse.y) * 0.05;
      camera.position.set(tmpV.x + mouse.x * 0.5, tmpV.y - mouse.y * 0.35, tmpV.z);

      var lookT = Math.min(1, t + 0.085);
      curve.getPointAt(lookT, lookV);
      if (w4 > 0) lookV.lerp(coreEnd, w4 * 0.85);
      camera.lookAt(lookV);

      if (railFill) railFill.style.height = (t * 100).toFixed(1) + '%';
    }

    function lerpAngle(a, b, t) {
      var d = (b - a) % (Math.PI * 2);
      if (d > Math.PI) d -= Math.PI * 2;
      if (d < -Math.PI) d += Math.PI * 2;
      return a + d * t;
    }

    /* ---------- ScrollTrigger: pin + scrub ---------- */
    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: stageEl.parentElement,   /* .tx-experience */
        start: 'top top',
        end: '+=420%',
        pin: stageEl,
        scrub: 0.9,
        anticipatePin: 1,
        invalidateOnRefresh: true
      }
    });
    tl.to(progress, { t: 1, ease: 'none', duration: 1 }, 0);

    /* hero copy exits early */
    var hero = stageEl.querySelector('.tx-hero');
    var cue = stageEl.querySelector('.tx-scrollcue');
    if (hero) tl.to(hero, { autoAlpha: 0, y: -60, ease: 'power1.in', duration: 0.08 }, 0.005);
    if (cue) tl.to(cue, { autoAlpha: 0, duration: 0.04 }, 0.002);

    /* stage copy blocks, choreographed inside the same scrub */
    var windows = [
      { sel: '[data-tx-copy="1"]', inAt: 0.085, outAt: 0.28 },
      { sel: '[data-tx-copy="2"]', inAt: 0.32, outAt: 0.52 },
      { sel: '[data-tx-copy="3"]', inAt: 0.56, outAt: 0.76 },
      { sel: '[data-tx-copy="4"]', inAt: 0.82, outAt: 1.01 }
    ];
    windows.forEach(function (wdef) {
      var el = stageEl.querySelector(wdef.sel);
      if (!el) return;
      tl.fromTo(el, { autoAlpha: 0, y: 44 }, { autoAlpha: 1, y: 0, ease: 'power2.out', duration: 0.05 }, wdef.inAt);
      if (wdef.outAt <= 1) {
        tl.to(el, { autoAlpha: 0, y: -36, ease: 'power2.in', duration: 0.045 }, wdef.outAt - 0.045);
      }
    });

    /* ---------- Section reveals below the experience ---------- */
    gsap.utils.toArray('.tx-reveal').forEach(function (el) {
      gsap.fromTo(el, { autoAlpha: 0, y: 34 }, {
        autoAlpha: 1, y: 0, duration: 1.1, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 86%', once: true }
      });
    });

    /* ============================================================
       RENDER LOOP
       ============================================================ */
    var perf = { now: 0 };
    var running = true;
    var inView = true;
    var ticking = false;

    /* stop burning frames once the reader is past the experience */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        if (inView && running && !ticking) requestAnimationFrame(tick);
      }, { rootMargin: '120px 0px' });
      io.observe(stageEl.parentElement);
    }

    document.addEventListener('visibilitychange', function () {
      running = !document.hidden;
      if (running && inView && !ticking) requestAnimationFrame(tick);
    });

    function tick(now) {
      if (!running || !inView) { ticking = false; return; }
      ticking = true;
      perf.now = now || 0;
      applyProgress(progress.t);
      window.__tx.t = progress.t;
      renderer.render(scene, camera);
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);

    window.addEventListener('resize', function () {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    });
  });
})();
